/**
 * Strategy Team Query Hooks
 *
 * Dedicated React Query hooks for the /strategy* routes:
 *   - /strategy                     (StrategyDashboardScreen)
 *   - /strategy-organizations       (OrganizationsScreen)
 *   - /performance-deviation        (PerformanceDeviationScreen)
 *   - /organizations-list           (OrganizationsListScreen)
 *   - /pending-approvals            (PendingApprovalsScreen)
 *
 * Why a dedicated file: the strategy dashboard bundles 5 parallel list calls
 * + a comparison endpoint; the audit flagged every screen in this cluster as
 * missing React Query (while the April 10 audit migrated 6 non-strategy list
 * screens). Centralizing here keeps the query keys and stale times aligned
 * across screens that share entities (e.g. `/strategy` and
 * `/strategy-organizations` both read orgs — they should share cache).
 */

import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  organizationsApi,
  goalsApi,
  initiativesApi,
  kpisApi,
  schedulesApi,
  usersApi,
  changeRequestsApi,
  risksApi,
  organizationalUnitsApi,
  servicesApi,
  processesApi,
  subProcessesApi,
  employeesApi,
  StrategyOverview,
} from '../../services/apiService';
import { queryKeys, STALE_TIMES } from '../../providers/QueryProvider';
import {
  OrganizationList,
  OrganizationComparison,
  StrategicGoalList,
  InitiativeList,
  KPIList,
  ChangeRequestList,
  PaginatedResponse,
} from '../../types';

// Page size for strategy-dashboard list calls. All derived stats (pending
// counts, status breakdown, per-org achievement map) assume a complete
// dataset — DRF defaults to 20 per page which silently breaks the totals.
const STRATEGY_PAGE_SIZE = 1000;

// ---------------------------------------------------------------------------
// Organizations (shared across PerformanceDeviation, Orgs, OrgsList, etc.)
// ---------------------------------------------------------------------------

export function useStrategyOrganizationsQuery(year?: number | null) {
  return useQuery({
    queryKey: [...queryKeys.organizations.list(), { year: year ?? null }],
    queryFn: async () => {
      const data = await organizationsApi.list(year ? { year } : undefined);
      return (data.results ?? []) as OrganizationList[];
    },
    staleTime: STALE_TIMES.ORGANIZATIONS,
    // Keep prior year's rows visible while a new year fetches — avoids
    // blanking the table on year switch and on cold revisits where the
    // previous data was still in cache.
    placeholderData: keepPreviousData,
  });
}

// ---------------------------------------------------------------------------
// Per-organization goals (OrganizationsListScreen expansion)
// ---------------------------------------------------------------------------

export function useOrganizationGoalsQuery(orgId: number | null, year?: number | null) {
  return useQuery({
    queryKey: ['strategy', 'org-goals', orgId, year ?? null],
    queryFn: async () => {
      if (!orgId) return [] as StrategicGoalList[];
      const data = await goalsApi.list({
        department_id: orgId,
        page_size: 100,
        ...(year ? { year } : {}),
      });
      return (data.results ?? []) as StrategicGoalList[];
    },
    enabled: orgId != null,
    staleTime: STALE_TIMES.GOALS,
  });
}

// ---------------------------------------------------------------------------
// Strategy dashboard — 5 parallel list calls + optional users count
// ---------------------------------------------------------------------------

export interface StrategyDashboardData {
  organizations: OrganizationList[];
  organizationsCount: number;
  goals: StrategicGoalList[];
  goalsCount: number;
  initiatives: InitiativeList[];
  initiativesCount: number;
  kpis: KPIList[];
  kpisCount: number;
  schedules: any[];
  usersCount: number;
  // Extra entity counts powering the Figma stat-card rows. Fetched via
  // allSettled so a single 403 for a role-restricted entity does not kill
  // the whole dashboard — the corresponding tile renders as 0 instead.
  risksCount: number;
  unitsCount: number;
  servicesCount: number;
  processesCount: number;
  subProcessesCount: number;
  employeesCount: number;
}

/**
 * @deprecated Prefer the per-pillar hooks below
 * (`useStrategyOrgListQuery`, `useStrategyGoalsListQuery`,
 *  `useStrategyInitiativesListQuery`, `useStrategyKpisListQuery`,
 *  `useDueReadingsQuery`, `useEntityCountsQuery`).
 *
 * Bundling 5 paginated list calls + 7 count calls + a 50-page schedules
 * loop into one query forces every pillar to wait for the slowest call
 * and blocks the whole /strategy page behind a single spinner. The new
 * hooks let each pillar render its own skeleton + content independently.
 *
 * Kept for back-compat with any caller that still consumes the bundled
 * `StrategyDashboardData` shape.
 */
export function useStrategyDashboardQuery(year?: number | null) {
  return useQuery({
    queryKey: ['strategy', 'dashboard', { year: year ?? null }],
    queryFn: async (): Promise<StrategyDashboardData> => {
      const baseParams: Record<string, any> = { page_size: STRATEGY_PAGE_SIZE };
      if (year) baseParams.year = year;

      // Backend caps page_size at 100 for schedules — paginate across ALL pages
      // so the due-readings card sees today/week/month buckets across every
      // org rather than just the first 100 (oldest) rows.
      async function fetchAllSchedules(): Promise<any[]> {
        const all: any[] = [];
        let pageNum = 1;
        // Hard-cap pages to avoid runaway loops; 50×100 = 5,000 schedules.
        for (let i = 0; i < 50; i++) {
          const resp = await schedulesApi.list({ ...baseParams, page: pageNum }) as PaginatedResponse<any>;
          if (resp?.results?.length) all.push(...resp.results);
          if (!resp?.next) break;
          pageNum++;
        }
        return all;
      }

      const [orgsData, goalsData, initData, kpisData, schedulesAll] = await Promise.all([
        organizationsApi.list(baseParams) as Promise<PaginatedResponse<OrganizationList>>,
        goalsApi.list(baseParams) as Promise<PaginatedResponse<StrategicGoalList>>,
        initiativesApi.list(baseParams) as Promise<PaginatedResponse<InitiativeList>>,
        kpisApi.list(baseParams) as Promise<PaginatedResponse<KPIList>>,
        fetchAllSchedules(),
      ]);
      const schedulesData = { results: schedulesAll, count: schedulesAll.length, next: null, previous: null } as PaginatedResponse<any>;

      // Count-only params — DRF returns total `count` regardless of page_size.
      const countParams: Record<string, any> = { page_size: 1 };
      if (year) countParams.year = year;

      // Each of these may 403 for certain roles. allSettled lets us surface
      // partial data instead of the whole dashboard blanking out.
      const [
        usersRes,
        risksRes,
        unitsRes,
        servicesRes,
        processesRes,
        subProcessesRes,
        employeesRes,
      ] = await Promise.allSettled([
        usersApi.list(),
        risksApi.list(countParams),
        organizationalUnitsApi.list(countParams),
        servicesApi.list(countParams),
        processesApi.list(countParams),
        subProcessesApi.list(countParams),
        employeesApi.list(countParams),
      ]);

      const countFrom = (res: PromiseSettledResult<any>): number =>
        res.status === 'fulfilled' ? (res.value?.count ?? 0) : 0;

      return {
        organizations: (orgsData.results ?? []) as OrganizationList[],
        organizationsCount: orgsData.count ?? 0,
        goals: (goalsData.results ?? []) as StrategicGoalList[],
        goalsCount: goalsData.count ?? 0,
        initiatives: (initData.results ?? []) as InitiativeList[],
        initiativesCount: initData.count ?? 0,
        kpis: (kpisData.results ?? []) as KPIList[],
        kpisCount: kpisData.count ?? 0,
        schedules: (schedulesData.results ?? []) as any[],
        usersCount: countFrom(usersRes),
        risksCount: countFrom(risksRes),
        unitsCount: countFrom(unitsRes),
        servicesCount: countFrom(servicesRes),
        processesCount: countFrom(processesRes),
        subProcessesCount: countFrom(subProcessesRes),
        employeesCount: countFrom(employeesRes),
      };
    },
    staleTime: STALE_TIMES.DASHBOARD,
  });
}

// ---------------------------------------------------------------------------
// Per-pillar queries — each /strategy pillar fetches only what it needs.
//
// All of these:
//   - share the `['strategy', ...]` queryKey prefix so YearContext can
//     selectively invalidate the strategy family on year change
//   - use `placeholderData: keepPreviousData` so picking a new year keeps
//     the prior year's content visible while the new request flies
//   - default to STALE_TIMES.DASHBOARD (5 min)
// ---------------------------------------------------------------------------

export function useStrategyOrgListQuery(year?: number | null) {
  return useQuery({
    queryKey: ['strategy', 'org-list', { year: year ?? null }],
    queryFn: async () => {
      const params: Record<string, any> = { page_size: STRATEGY_PAGE_SIZE };
      if (year) params.year = year;
      const data = await organizationsApi.list(params) as PaginatedResponse<OrganizationList>;
      return {
        results: (data.results ?? []) as OrganizationList[],
        count: data.count ?? 0,
      };
    },
    staleTime: STALE_TIMES.ORGANIZATIONS,
    placeholderData: keepPreviousData,
  });
}

export function useStrategyGoalsListQuery(year?: number | null) {
  return useQuery({
    queryKey: ['strategy', 'goals-list', { year: year ?? null }],
    queryFn: async () => {
      const params: Record<string, any> = { page_size: STRATEGY_PAGE_SIZE };
      if (year) params.year = year;
      const data = await goalsApi.list(params) as PaginatedResponse<StrategicGoalList>;
      return {
        results: (data.results ?? []) as StrategicGoalList[],
        count: data.count ?? 0,
      };
    },
    staleTime: STALE_TIMES.DASHBOARD,
    placeholderData: keepPreviousData,
  });
}

export function useStrategyInitiativesListQuery(year?: number | null) {
  return useQuery({
    queryKey: ['strategy', 'initiatives-list', { year: year ?? null }],
    queryFn: async () => {
      const params: Record<string, any> = { page_size: STRATEGY_PAGE_SIZE };
      if (year) params.year = year;
      const data = await initiativesApi.list(params) as PaginatedResponse<InitiativeList>;
      return {
        results: (data.results ?? []) as InitiativeList[],
        count: data.count ?? 0,
      };
    },
    staleTime: STALE_TIMES.DASHBOARD,
    placeholderData: keepPreviousData,
  });
}

export function useStrategyKpisListQuery(year?: number | null) {
  return useQuery({
    queryKey: ['strategy', 'kpis-list', { year: year ?? null }],
    queryFn: async () => {
      const params: Record<string, any> = { page_size: STRATEGY_PAGE_SIZE };
      if (year) params.year = year;
      const data = await kpisApi.list(params) as PaginatedResponse<KPIList>;
      return {
        results: (data.results ?? []) as KPIList[],
        count: data.count ?? 0,
      };
    },
    staleTime: STALE_TIMES.DASHBOARD,
    placeholderData: keepPreviousData,
  });
}

/**
 * Schedules used by the due-readings card. The legacy
 * `useStrategyDashboardQuery` looped up to 50 pages × 100 rows; that's
 * heavy and dominated TTI. We cap at 5 pages here. A backend
 * `/schedules/due-summary/?year=` aggregation endpoint is the real fix
 * and tracked as a follow-up ticket.
 */
const DUE_READINGS_PAGE_LIMIT = 5;
const DUE_READINGS_PAGE_SIZE = 100;

export function useDueReadingsQuery(year?: number | null) {
  return useQuery({
    queryKey: ['strategy', 'due-readings', { year: year ?? null }],
    queryFn: async () => {
      // The card buckets into today / this-week / this-month, so the
      // relevant window is the current calendar month. Limiting the
      // server-side fetch to that range + is_completed=false avoids the
      // pagination cap leaking only old completed schedules into the
      // client buckets.
      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      const toIso = (d: Date) =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

      const baseParams: Record<string, any> = {
        page_size: DUE_READINGS_PAGE_SIZE,
        is_completed: false,
        scheduled_date_gte: toIso(monthStart),
        scheduled_date_lte: toIso(monthEnd),
      };
      if (year) baseParams.year = year;
      const all: any[] = [];
      let pageNum = 1;
      for (let i = 0; i < DUE_READINGS_PAGE_LIMIT; i++) {
        const resp = await schedulesApi.list({ ...baseParams, page: pageNum }) as PaginatedResponse<any>;
        if (resp?.results?.length) all.push(...resp.results);
        if (!resp?.next) break;
        pageNum++;
      }
      return all;
    },
    staleTime: STALE_TIMES.SCHEDULES,
    placeholderData: keepPreviousData,
  });
}

export interface StrategyEntityCounts {
  usersCount: number;
  risksCount: number;
  unitsCount: number;
  servicesCount: number;
  processesCount: number;
  subProcessesCount: number;
  employeesCount: number;
}

/**
 * Counts-only batch for the secondary stat tiles. Uses `Promise.allSettled`
 * so a 403 on any single endpoint (role-restricted) renders that tile as 0
 * instead of failing the whole batch.
 */
export function useEntityCountsQuery(year?: number | null) {
  return useQuery({
    queryKey: ['strategy', 'entity-counts', { year: year ?? null }],
    queryFn: async (): Promise<StrategyEntityCounts> => {
      const countParams: Record<string, any> = { page_size: 1 };
      if (year) countParams.year = year;

      const [
        usersRes,
        risksRes,
        unitsRes,
        servicesRes,
        processesRes,
        subProcessesRes,
        employeesRes,
      ] = await Promise.allSettled([
        usersApi.list(),
        risksApi.list(countParams),
        organizationalUnitsApi.list(countParams),
        servicesApi.list(countParams),
        processesApi.list(countParams),
        subProcessesApi.list(countParams),
        employeesApi.list(countParams),
      ]);

      const countFrom = (res: PromiseSettledResult<any>): number =>
        res.status === 'fulfilled' ? (res.value?.count ?? 0) : 0;

      return {
        usersCount: countFrom(usersRes),
        risksCount: countFrom(risksRes),
        unitsCount: countFrom(unitsRes),
        servicesCount: countFrom(servicesRes),
        processesCount: countFrom(processesRes),
        subProcessesCount: countFrom(subProcessesRes),
        employeesCount: countFrom(employeesRes),
      };
    },
    staleTime: STALE_TIMES.DASHBOARD,
    placeholderData: keepPreviousData,
  });
}

// ---------------------------------------------------------------------------
// Aggregated executive dashboard overview (all orgs) — backend-computed totals
//
// Replaces the earlier client-side reduce over paginated lists. The backend's
// /organizations/overview/ sums counts across every SSO department, computes
// stage-based vision + commitment, counts excellent KPIs via the cumulative
// engine, and returns simple-averaged achievement / deviation so the strategy
// dashboard cards reflect the full 15-org portfolio — not just page 1.
// ---------------------------------------------------------------------------

export function useStrategyOverviewQuery(year?: number | null) {
  return useQuery({
    queryKey: ['strategy', 'overview', { year: year ?? null }],
    queryFn: async (): Promise<StrategyOverview> => {
      const params = year ? { year } : undefined;
      return await organizationsApi.getOverview(params);
    },
    staleTime: STALE_TIMES.DASHBOARD,
  });
}

// ---------------------------------------------------------------------------
// Comparison chart (sort-aware queryKey so each sort is its own cache entry)
// ---------------------------------------------------------------------------

export interface UseStrategyComparisonOptions {
  sortBy: string;
  order: 'asc' | 'desc';
  enabled: boolean;
}

export function useStrategyComparisonQuery({ sortBy, order, enabled }: UseStrategyComparisonOptions) {
  return useQuery({
    queryKey: ['strategy', 'comparison', { sortBy, order }],
    queryFn: async () => {
      const data = await organizationsApi.getComparison({ sort_by: sortBy, order });
      return data as OrganizationComparison[];
    },
    enabled,
    staleTime: STALE_TIMES.ORGANIZATIONS,
    // Keeps cached rows visible during sort changes / year invalidations
    // so consumers don't briefly fall back to `isPending: true` and
    // re-show their loading UI on revisit.
    placeholderData: keepPreviousData,
  });
}

// ---------------------------------------------------------------------------
// Schedules org-summary (powers /strategy/schedules-management)
//
// Server-side aggregated view: per-org KPI counts + due / completed totals
// for the picked year (year is auto-injected by the API interceptor from
// YearContext, so we only key on the explicit filters here).
// ---------------------------------------------------------------------------

export interface UseOrgScheduleSummaryOptions {
  organization: string | number | null;
  page: number;
  pageSize: number;
  year?: number | null;
  enabled?: boolean;
}

// Shape of the /schedules/org-summary/ response. Typed loosely as `any[]`
// for results because the underlying serializer adds new fields ad-hoc;
// the consumer (SchedulesManagementScreen) maps fields by name.
export interface OrgScheduleSummaryResponse {
  results: any[];
  count: number;
  global_stats?: {
    total_kpis: number;
    total_readings: number;
    due_and_completed: number;
    overdue_count: number;
  };
}

export function useOrgScheduleSummaryQuery({
  organization,
  page,
  pageSize,
  year,
  enabled = true,
}: UseOrgScheduleSummaryOptions) {
  return useQuery<OrgScheduleSummaryResponse>({
    queryKey: ['strategy', 'schedules-org-summary', { organization: organization || null, page, pageSize, year: year ?? null }],
    queryFn: async () => {
      const params: Record<string, any> = { page, page_size: pageSize };
      if (organization) params.organization = organization;
      const resp = await schedulesApi.orgSummary(params);
      return resp as OrgScheduleSummaryResponse;
    },
    enabled,
    staleTime: STALE_TIMES.SCHEDULES,
    placeholderData: keepPreviousData,
  });
}

// ---------------------------------------------------------------------------
// Pending approvals (org_executive screen)
// ---------------------------------------------------------------------------

export interface PendingApprovalsBundle {
  approvals: any; // shape from /organizations/pending-approvals/
  deletionRequests: ChangeRequestList[];
}

export function usePendingApprovalsQuery(enabled: boolean = true) {
  return useQuery({
    queryKey: ['strategy', 'pending-approvals'],
    queryFn: async (): Promise<PendingApprovalsBundle> => {
      const [approvals, deletionResp] = await Promise.all([
        organizationsApi.getPendingApprovals(),
        changeRequestsApi.list({ status: 'pending', request_type: 'delete' }),
      ]);
      // changeRequestsApi.list returns axios response, not just .data
      const deletionRequests: ChangeRequestList[] =
        (deletionResp as any)?.data?.results
        ?? (deletionResp as any)?.results
        ?? [];
      return { approvals, deletionRequests };
    },
    enabled,
    staleTime: 2 * 60 * 1000, // 2 min — approvals turn over faster than orgs
  });
}

// ---------------------------------------------------------------------------
// Mutations — invalidate the pending-approvals cache + dependent lists
// ---------------------------------------------------------------------------

function invalidatePendingAndDependents(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ['strategy', 'pending-approvals'] });
  queryClient.invalidateQueries({ queryKey: ['strategy', 'dashboard'] });
  queryClient.invalidateQueries({ queryKey: queryKeys.organizations.all });
  queryClient.invalidateQueries({ queryKey: queryKeys.goals.all });
  queryClient.invalidateQueries({ queryKey: queryKeys.initiatives.all });
  queryClient.invalidateQueries({ queryKey: queryKeys.kpis.all });
}

export function useBatchApproveMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (departmentId: number) => organizationsApi.batchApprove(departmentId),
    onSuccess: () => invalidatePendingAndDependents(queryClient),
  });
}

export function useApproveDeletionMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => changeRequestsApi.approve(id),
    onSuccess: () => invalidatePendingAndDependents(queryClient),
  });
}

export function useRejectDeletionMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      changeRequestsApi.reject(id, reason),
    onSuccess: () => invalidatePendingAndDependents(queryClient),
  });
}
